'use client';

import { Suspense, useState } from 'react';
import Link from 'next/link';
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertCircle, ArrowLeft, Check, LoaderCircleIcon, Mail } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { apiFetch } from '@/lib/api';
import { useTranslation } from '@/hooks/useTranslation';
import { Alert, AlertIcon, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import AuthShell from '@/components/auth/AuthShell';

export default function ResetPasswordClient() {
  const { t } = useTranslation();
  // Auth-Seiten sind pre-login → kein Inline-Editor; immer reinen String liefern.
  const tt = (key, def) => t(key, { defaultValue: def, editable: false });
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [success, setSuccess] = useState(null);

  const formSchema = z.object({
    email: z.string().email({
      message: tt('auth.reset.invalid_email', 'Bitte gib eine gültige E-Mail-Adresse ein.'),
    }),
  });

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
    },
  });

  const onSubmit = async (values) => {
    try {
      setIsProcessing(true);
      setError(null);
      setSuccess(null);

      const response = await apiFetch('/api/auth/reset-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...values, origin: window.location.origin }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.message);
        return;
      }

      setSuccess(data.message);
      form.reset();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : tt('auth.reset.unexpected_error', 'Ein unerwarteter Fehler ist aufgetreten. Bitte versuche es erneut.'),
      );
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Suspense>
      <AuthShell
        title={tt('auth.reset.title', 'Passwort zurücksetzen')}
        subtitle={tt(
          'auth.reset.subtitle',
          'Gib deine E-Mail-Adresse ein, um einen Link zum Zurücksetzen zu erhalten.',
        )}
        documentTitle={tt('auth.reset.title', 'Passwort zurücksetzen')}
      >
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="block w-full space-y-5">
            {error && (
              <Alert variant="destructive" onClose={() => setError(null)}>
                <AlertIcon>
                  <AlertCircle />
                </AlertIcon>
                <AlertTitle>{error}</AlertTitle>
              </Alert>
            )}

            {success && (
              <Alert onClose={() => setSuccess(null)}>
                <AlertIcon>
                  <Check />
                </AlertIcon>
                <AlertTitle>{success}</AlertTitle>
              </Alert>
            )}

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{tt('auth.reset.email_label', 'E-Mail')}</FormLabel>
                  <div className="relative">
                    <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <FormControl>
                      <Input
                        type="email"
                        placeholder={tt('auth.reset.email_placeholder', 'E-Mail-Adresse eingeben')}
                        className="ps-9"
                        disabled={!!success || isProcessing}
                        {...field}
                      />
                    </FormControl>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button
              type="submit"
              disabled={!!success || isProcessing}
              className="w-full"
            >
              {isProcessing ? (
                <LoaderCircleIcon className="animate-spin" />
              ) : null}
              {tt('auth.reset.submit', 'Link senden')}
            </Button>

            <div className="space-y-3">
              <Button type="button" variant="outline" className="w-full" asChild>
                <Link href="/signin">
                  <ArrowLeft className="size-3.5" />{' '}
                  {tt('auth.reset.back', 'Zurück zum Login')}
                </Link>
              </Button>
            </div>
          </form>
        </Form>
      </AuthShell>
    </Suspense>
  );
}
